import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Container, SpaceBetween } from '@amzn/awsui-components-core';
import {
  Content,
  ContentAudience,
  DuplicationCheckStatus,
  DuplicationResultsResponse,
  ExternalOperationName,
  KnowledgeSource,
  OperationCount,
  ScoredArticleWithAuthors,
} from '@amzn/awscat-pattern-library-service-model';
import DuplicateCheckResults from './DuplicateCheckResults';
import { DuplicateScanResultItem } from '../../../duplicate-scan-results/DuplicateScanResultsView';
import DuplicateCheckLoadingScreen, { DuplicateCheckFooter } from './DuplicateCheckLoadingScreen';
import { MplServiceClientFactory } from '../../../../api/MplServiceClientFactory';
import { getContentPath } from '../../../../common/ContentPaths';
import {
  recordDuplicationScanComplete,
  recordDuplicateResultClickthrough,
} from '../../../../monitoring/PreapprovalTelemetry';
import { buildContributorMailtoLink } from './utils/EmailUtils';
import FlashbarMessageBroker from '../../../../common/FlashbarMessageBroker';

interface Step4DuplicateCheckProps {
  readonly content: Content;
  readonly contentAudience: ContentAudience;
  readonly onDuplicateCheckComplete?: (results: DuplicateScanResultItem[]) => void;
  readonly onValidChange?: (valid: boolean) => void;
  readonly onSearchingChange?: (isSearching: boolean) => void;
  readonly onContentIdReceived?: (contentId: string, workflowId: number) => void;
  readonly contentId?: string;
  readonly onBeforeExternalNavigation: (url: string) => void;
}

const mapScoredArticleToResultItem = (
  article: ScoredArticleWithAuthors,
): DuplicateScanResultItem => {
  const whyFlagged =
    article.titleScore?.reason ||
    article.bodyScore?.matchedTopics?.map((t) => t.reason).join('; ') ||
    'Similar content detected';

  const authorEmails = article.authors
    ?.map((a) => a.email)
    .filter((email): email is string => !!email);

  // If source if APGL then ID = content ID. If source = AWS then its a URL already
  const url =
    article.source === KnowledgeSource.APGL
      ? `${window.location.origin}${getContentPath({ contentId: article.id, mode: 'publication' })}`
      : article.id;

  return {
    id: article.id,
    title: article.title,
    url,
    source: article.source,
    similarityMatch: Math.round(article.finalScore * 100),
    whyFlagged,
    canRequestContributor: article.source === KnowledgeSource.APGL,
    authorEmails,
  };
};

const Step4DuplicateCheck: React.FC<Step4DuplicateCheckProps> = ({
  content,
  contentAudience,
  onDuplicateCheckComplete,
  onValidChange,
  onSearchingChange,
  onContentIdReceived,
  contentId,
  onBeforeExternalNavigation,
}) => {
  const [isSearching, setIsSearching] = useState(true);
  const [duplicateResults, setDuplicateResults] = useState<DuplicateScanResultItem[]>([]);
  const [isContentHarmful, setIsContentHarmful] = useState(false);
  const [isContentInvalid, setIsContentInvalid] = useState(false);
  const [isUniqueConfirmed, setIsUniqueConfirmed] = useState(false);
  const [traceId, setTraceId] = useState('');
  const [duplicationStatus, setDuplicationStatus] = useState<DuplicationCheckStatus>(
    DuplicationCheckStatus.FAILED,
  );
  const [successRateTrackers, setSuccessRateTrackers] = useState<
    Partial<Record<ExternalOperationName, OperationCount>> | undefined
  >(undefined);
  const [scanKey, setScanKey] = useState(0);
  const resultsRef = useRef<DuplicateScanResultItem[]>([]);
  const contentIdRef = useRef<string | undefined>(contentId);
  const workflowIdRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    onSearchingChange?.(isSearching);
  }, [isSearching, onSearchingChange]);

  const handleUniqueConfirmedChange = useCallback(
    (confirmed: boolean) => {
      setIsUniqueConfirmed(confirmed);
      onValidChange?.(confirmed);
    },
    [onValidChange],
  );

  const handleRequestContributor = useCallback(
    (itemId: string) => {
      const matchingResult = duplicateResults.find((r) => r.id === itemId);
      if (!matchingResult) return; // Should never happen..

      try {
        const mailtoLink = buildContributorMailtoLink(
          matchingResult.title,
          itemId,
          matchingResult.authorEmails ?? [],
        );
        // Suppress the beforeunload guard before opening the mailto link
        // so the browser doesn't show a "leave page" dialog.
        onBeforeExternalNavigation(mailtoLink);
      } catch {
        FlashbarMessageBroker.create().pushErrorMessage('Failed to find content author details.');
      }
    },
    [duplicateResults, onBeforeExternalNavigation],
  );

  const handleRescan = useCallback(() => {
    setIsSearching(true);
    setDuplicateResults([]);
    setIsContentHarmful(false);
    setIsContentInvalid(false);
    setIsUniqueConfirmed(false);
    setDuplicationStatus(DuplicationCheckStatus.FAILED);
    setSuccessRateTrackers(undefined);
    resultsRef.current = [];
    onValidChange?.(false);
    setScanKey((prev) => prev + 1);
  }, [onValidChange]);

  useEffect(() => {
    const abortController = new AbortController();
    const mplServiceClient = MplServiceClientFactory.create();
    let flaggedHarmful = false;
    let flaggedInvalid = false;
    let lastEventStatus: DuplicationCheckStatus | undefined;
    let receivedTraceId = '';

    setIsContentHarmful(false);
    setIsContentInvalid(false);

    const handleDuplicationEvent = (event: DuplicationResultsResponse) => {
      if (abortController.signal.aborted) return;

      receivedTraceId = event.traceId;
      setTraceId(event.traceId);

      if (event.isContentHarmful) {
        flaggedHarmful = true;
        setIsContentHarmful(true);
      }
      if (event.isContentInvalid) {
        flaggedInvalid = true;
        setIsContentInvalid(true);
      }

      if (event.status) {
        lastEventStatus = event.status;
        setDuplicationStatus(event.status);
      }

      if (event.successRateTrackers) {
        setSuccessRateTrackers(event.successRateTrackers);
      }

      if (event.scoredArticles && event.scoredArticles.length > 0) {
        // We only take the top 5 results greater than or equal to 60%
        const mappedResults = event.scoredArticles
          .map(mapScoredArticleToResultItem)
          .filter((item) => item.similarityMatch >= 60)
          .sort((a, b) => b.similarityMatch - a.similarityMatch)
          .slice(0, 5);

        // Each event replaces the previous results so the last event
        // (typically from the DONE payload) is the source of truth.
        setDuplicateResults(() => {
          resultsRef.current = mappedResults;
          return mappedResults;
        });
      }
    };

    const performSearch = async () => {
      const startTime = performance.now();
      setIsUniqueConfirmed(false);
      let resolvedStatus: DuplicationCheckStatus | undefined;
      let failureReason: string | undefined;
      try {
        await mplServiceClient.updateProposal(
          content,
          contentAudience,
          contentId,
          handleDuplicationEvent,
          (id: string, wfId: number) => {
            contentIdRef.current = id;
            workflowIdRef.current = wfId;
            onContentIdReceived?.(id, wfId);
          },
          abortController.signal,
        );
        if (!abortController.signal.aborted) {
          resolvedStatus =
            lastEventStatus && lastEventStatus !== DuplicationCheckStatus.IN_PROGRESS
              ? lastEventStatus
              : DuplicationCheckStatus.SUCCESS;
          setDuplicationStatus(resolvedStatus);
          onDuplicateCheckComplete?.(resultsRef.current);
        }
      } catch (error) {
        failureReason = error instanceof Error ? error.message : String(error);
        if (!abortController.signal.aborted) {
          resolvedStatus =
            lastEventStatus && lastEventStatus !== DuplicationCheckStatus.IN_PROGRESS
              ? lastEventStatus
              : DuplicationCheckStatus.FAILED;
          setDuplicationStatus(resolvedStatus);
          // Keep any results received before the error
          onDuplicateCheckComplete?.(resultsRef.current);
        }
      } finally {
        if (!abortController.signal.aborted) {
          const durationMs = Math.round(performance.now() - startTime);
          if (contentIdRef.current && workflowIdRef.current !== undefined) {
            recordDuplicationScanComplete({
              contentId: contentIdRef.current,
              workflowId: workflowIdRef.current,
              traceId: receivedTraceId,
              durationMs,
              scanStatus: resolvedStatus ?? DuplicationCheckStatus.FAILED,
              isContentHarmful: flaggedHarmful,
              isContentInvalid: flaggedInvalid,
              failureReason,
            });
          }
          setIsSearching(false);

          // Notify parent of validity now that the scan is complete.
          // Confirmation is not required when the scan fully succeeded
          // with no results and no content flags.
          const confirmationNotRequired =
            (resolvedStatus ?? DuplicationCheckStatus.FAILED) === DuplicationCheckStatus.SUCCESS &&
            resultsRef.current.length === 0 &&
            !flaggedHarmful &&
            !flaggedInvalid;
          onValidChange?.(confirmationNotRequired);
        }
      }
    };

    performSearch();

    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanKey]);

  const getContentContext = () => ({
    contentId: contentIdRef.current ?? '',
    workflowId: workflowIdRef.current ?? 0,
    traceId,
  });

  return (
    <Container footer={isSearching ? <DuplicateCheckFooter /> : undefined}>
      <SpaceBetween size="l">
        {isSearching ? (
          <DuplicateCheckLoadingScreen />
        ) : (
          <DuplicateCheckResults
            results={duplicateResults}
            status={duplicationStatus}
            isContentHarmful={isContentHarmful}
            isContentInvalid={isContentInvalid}
            isUniqueContentConfirmed={isUniqueConfirmed}
            onUniqueContentConfirmedChange={handleUniqueConfirmedChange}
            onRequestContributor={handleRequestContributor}
            successRateTrackers={successRateTrackers}
            onRescan={handleRescan}
            onResultClick={(articleId) =>
              recordDuplicateResultClickthrough({
                ...getContentContext(),
                articleId,
              })
            }
            onScanFeedback={
              traceId
                ? (value, feedback) => ({
                    ...getContentContext(),
                    subType: 'DuplicationScanFeedback',
                    rating: value,
                    ...{ feedback },
                  })
                : undefined
            }
          />
        )}
      </SpaceBetween>
    </Container>
  );
};

export default Step4DuplicateCheck;
